import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ScrollText } from 'lucide-react'
import { pageWide } from '@/lib/form-styles'
import { AKTION_TEXT, mandantenendeApi, protokollText, type EndeProtokollZeile } from '../api/mandantenende.api'

/**
 * AP-42 — Löschprotokoll: Exporte, Vormerkungen und Löschungen aller Mandanten,
 * auch derer, die es nicht mehr gibt.
 *
 * Bewusst **keine** Auswertungsliste mit Filterleiste und Spaltenwahl (AP-42
 * V-12): ein Nachweis, der nur gelesen wird, wenige Zeilen im Jahr, neueste
 * zuerst.
 */
export default function OperatorLoeschprotokollPage() {
  const [zeilen, setZeilen] = useState<EndeProtokollZeile[]>([])
  const [seite, setSeite] = useState(1)
  const [seiten, setSeiten] = useState(1)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laden, setLaden] = useState(true)

  useEffect(() => {
    let aktiv = true
    setLaden(true)
    mandantenendeApi
      .protokoll(seite)
      .then((res) => {
        if (!aktiv) return
        setZeilen(res.data)
        setSeiten(Math.max(1, res.meta.totalPages))
        setFehler(null)
      })
      .catch(() => aktiv && setFehler('Das Protokoll konnte nicht geladen werden.'))
      .finally(() => aktiv && setLaden(false))
    return () => {
      aktiv = false
    }
  }, [seite])

  return (
    <div className={pageWide}>
      <div>
        <Link
          to="/admin/mandanten"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Mandanten
        </Link>
        <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
          <ScrollText size={26} className="text-primary" />
          Löschprotokoll
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gesamtexporte, Vormerkungen und Löschungen — auch von Mandanten, die es nicht mehr gibt.
        </p>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2">Zeitpunkt</th>
              <th className="px-4 py-2">Mandant</th>
              <th className="px-4 py-2">Vorgang</th>
              <th className="px-4 py-2">Einzelheiten</th>
              <th className="px-4 py-2">Durch</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {laden ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Laden…</td>
              </tr>
            ) : zeilen.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Noch keine Einträge.</td>
              </tr>
            ) : (
              zeilen.map((z) => (
                <tr key={z.id}>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-muted-foreground">
                    {new Date(z.createdAt).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-1.5">
                    <span className="font-medium text-foreground">{z.tenantName}</span>{' '}
                    <span className="font-mono text-xs text-muted-foreground">{z.subdomain}</span>
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-1.5 ${
                      z.action === 'DELETION_FAILED'
                        ? 'font-medium text-destructive'
                        : z.action === 'DELETION_COMPLETED'
                          ? 'font-medium text-foreground'
                          : 'text-foreground'
                    }`}
                  >
                    {AKTION_TEXT[z.action] ?? z.action}
                  </td>
                  <td className="px-4 py-1.5 text-muted-foreground">{protokollText(z)}</td>
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">{z.operatorEmail ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {seiten > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button
            type="button"
            disabled={seite <= 1}
            onClick={() => setSeite((s) => s - 1)}
            className="rounded-md border border-border px-3 py-1.5 disabled:opacity-50"
          >
            Zurück
          </button>
          <span className="text-muted-foreground">
            Seite {seite} von {seiten}
          </span>
          <button
            type="button"
            disabled={seite >= seiten}
            onClick={() => setSeite((s) => s + 1)}
            className="rounded-md border border-border px-3 py-1.5 disabled:opacity-50"
          >
            Weiter
          </button>
        </div>
      )}
    </div>
  )
}
